import React, { useState, useEffect } from 'react';
import { Mail, Phone, ChevronRight, ChevronLeft, BookOpen, Plus, Sparkles } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { cn } from '@/lib/utils';
import { useCommunicationTemplates } from '@/hooks/useCommunicationTemplates';
import { useAuth } from '@/context/AuthContext';
import { validateEmailSettings } from '@/services/emailSendingService';
import EmailTemplateView from './EmailTemplateView';
import CallScriptView from './CallScriptView';
import TemplateManager from '@/components/communication/TemplateManager';
import { CommunicationTemplate } from '@/services/communication/templates';

interface CommunicationSidePanelProps {
  leadId: string;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}

const CommunicationSidePanel: React.FC<CommunicationSidePanelProps> = ({
  leadId,
  isCollapsed,
  onToggleCollapse
}) => {
  const { profile } = useAuth();
  const [activeTab, setActiveTab] = useState<'email' | 'call'>('email');
  const [emailMainTab, setEmailMainTab] = useState<'new' | 'templates'>('new');
  const [callMainTab, setCallMainTab] = useState<'new' | 'templates'>('new');
  const [emailValidation, setEmailValidation] = useState<any>(null);

  const {
    lead,
    emailTemplate,
    callScript,
    isLoadingEmail,
    isLoadingCall,
    handleGenerateEmailTemplate,
    handleGenerateEmailWithPrompt,
    handleGenerateCallScript,
    handleGenerateCallScriptWithPrompt,
    saveCurrentEmailTemplate,
    saveCurrentCallScript
  } = useCommunicationTemplates(leadId);

  useEffect(() => {
    if (profile?.company_id) {
      checkEmailSettings();
    }
  }, [profile?.company_id]);

  const checkEmailSettings = async () => {
    if (!profile?.company_id) return;
    try {
      const validation = await validateEmailSettings(profile.company_id);
      setEmailValidation(validation);
    } catch (error) {
      console.error('Error checking email settings:', error);
    }
  };

  const handleSelectTemplate = (template: CommunicationTemplate) => {
    if (template.type === 'email') {
      saveCurrentEmailTemplate(template.content);
      setEmailMainTab('new');
    } else {
      saveCurrentCallScript(template.content);
      setCallMainTab('new');
    }
  };

  if (isCollapsed) {
    return (
      <div className="fixed right-0 top-14 h-[calc(100vh-3.5rem)] w-16 bg-card border-l-4 border-primary/20 shadow-lg z-40 flex flex-col items-center py-6 gap-6">
        <Button
          variant="ghost"
          size="icon"
          onClick={onToggleCollapse}
          className="h-12 w-12 hover:bg-primary/10 hover:scale-110 transition-all"
          aria-label="Expand communication panel"
        >
          <ChevronLeft className="h-5 w-5" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => {
            onToggleCollapse();
            setActiveTab('email');
          }}
          className={cn(
            "h-12 w-12 hover:bg-primary/10 hover:scale-110 transition-all",
            activeTab === 'email' && "bg-primary/15 ring-2 ring-primary/30"
          )}
          aria-label="Email communication"
        >
          <Mail className="h-5 w-5" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => {
            onToggleCollapse();
            setActiveTab('call');
          }}
          className={cn(
            "h-12 w-12 hover:bg-primary/10 hover:scale-110 transition-all",
            activeTab === 'call' && "bg-primary/15 ring-2 ring-primary/30"
          )}
          aria-label="Call communication"
        >
          <Phone className="h-5 w-5" />
        </Button>
      </div>
    );
  }

  return (
    <>
      {/* Backdrop overlay */}
      {!isCollapsed && (
        <div 
          className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40 transition-opacity duration-300"
          onClick={onToggleCollapse}
          aria-hidden="true"
        />
      )}
      
      {/* Side Panel with animation */}
      <div className={cn(
        "fixed right-0 top-14 h-[calc(100vh-3.5rem)] bg-card border-l-4 border-primary/20 shadow-[0_0_50px_rgba(0,0,0,0.15)] z-50 overflow-y-auto transition-all duration-300 ease-in-out",
        isCollapsed ? "w-16" : "w-[400px]"
      )}>
        <Card className="border-0 shadow-none rounded-none h-full">
          <CardHeader className="sticky top-0 bg-gradient-to-r from-primary/5 via-accent/5 to-primary/5 backdrop-blur-sm z-10 border-b pb-6 pt-6 px-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gradient-to-br from-primary/10 to-accent/10 rounded-lg">
                  <Sparkles className="h-5 w-5 text-primary" />
                </div>
                <CardTitle className="text-xl font-bold tracking-tight">Communication</CardTitle>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={onToggleCollapse}
                className="h-10 w-10 hover:bg-primary/10 transition-all"
                aria-label="Close communication panel"
                aria-expanded={!isCollapsed}
              >
                <ChevronRight className="h-5 w-5" />
              </Button>
            </div>
            
            {/* Mode toggle - only show when a tab is active */}
            {activeTab && (
              <div className="flex items-center justify-between gap-3 p-3 bg-gradient-to-r from-muted/50 to-muted/30 rounded-xl border border-border/50">
                <span className="text-sm font-medium text-foreground flex-1">
                  {activeTab === 'email' 
                    ? (emailMainTab === 'new' ? 'Create Email' : 'Email Library')
                    : (callMainTab === 'new' ? 'Create Script' : 'Script Library')
                  }
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    if (activeTab === 'email') {
                      setEmailMainTab(emailMainTab === 'new' ? 'templates' : 'new');
                    } else {
                      setCallMainTab(callMainTab === 'new' ? 'templates' : 'new');
                    }
                  }}
                  className="h-9 px-4 gap-2 hover:bg-accent hover:shadow-md transition-all"
                >
                  {activeTab === 'email' 
                    ? (emailMainTab === 'new' ? <BookOpen className="h-4 w-4" /> : <Plus className="h-4 w-4" />)
                    : (callMainTab === 'new' ? <BookOpen className="h-4 w-4" /> : <Plus className="h-4 w-4" />)
                  }
                  <span className="text-sm font-medium">
                    {activeTab === 'email'
                      ? (emailMainTab === 'new' ? 'View Saved' : 'Create New')
                      : (callMainTab === 'new' ? 'View Saved' : 'Create New')
                    }
                  </span>
                </Button>
              </div>
            )}
          </CardHeader>
          <CardContent className="p-8 space-y-6">
            <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'email' | 'call')}>
              <TabsList className="grid w-full grid-cols-2 mb-6 h-12">
                <TabsTrigger 
                  value="email" 
                  className="text-base font-medium gap-3 data-[state=active]:bg-gradient-to-r data-[state=active]:from-primary/10 data-[state=active]:to-accent/10 data-[state=active]:shadow-sm transition-all"
                >
                  <Mail className="h-5 w-5" />
                  Email
                </TabsTrigger>
                <TabsTrigger 
                  value="call" 
                  className="text-base font-medium gap-3 data-[state=active]:bg-gradient-to-r data-[state=active]:from-primary/10 data-[state=active]:to-accent/10 data-[state=active]:shadow-sm transition-all"
                >
                  <Phone className="h-5 w-5" />
                  Call
                </TabsTrigger>
              </TabsList>

              <TabsContent value="email" className="mt-4 space-y-6">
                {emailMainTab === 'new' ? (
                <EmailTemplateView
                  emailTemplate={emailTemplate}
                  isLoading={isLoadingEmail}
                  onGenerate={handleGenerateEmailTemplate}
                  onGenerateWithPrompt={handleGenerateEmailWithPrompt}
                  leadStatus={lead?.status}
                  lead={lead}
                  emailValidation={emailValidation}
                  compact
                />
              ) : (
                <TemplateManager
                  type="email"
                  onSelectTemplate={handleSelectTemplate}
                  leadId={leadId}
                />
              )}
            </TabsContent>

            <TabsContent value="call" className="mt-4 space-y-6">
              {callMainTab === 'new' ? (
                <CallScriptView
                  callScript={callScript}
                  isLoading={isLoadingCall}
                  onGenerate={handleGenerateCallScript}
                  onGenerateWithPrompt={handleGenerateCallScriptWithPrompt}
                  leadStatus={lead?.status}
                  compact
                />
              ) : (
                <TemplateManager
                  type="call"
                  onSelectTemplate={handleSelectTemplate}
                  leadId={leadId}
                />
              )}
            </TabsContent>
          </Tabs>
          </CardContent>
        </Card>
      </div>
    </>
  );
};

export default CommunicationSidePanel;
